import { ApiEnvelope, GitHubPullRequest } from '../../domain/types';
import type { GitHubConnectionAck } from '@zunelo/shared';
import {
  IGitHubClient,
  HttpClientOptions,
  PublishAiReviewInput,
  PublishAiReviewResult,
  AiReviewJob,
  GitHubMergeStrategy,
} from './types';
import { parseGithubEnvelope } from './githubEnvelope';
import { createHttpTransport, type HttpTransport } from './httpTransport';

export class HttpGitHubClient implements IGitHubClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions | string = {}) {
    this.transport = createHttpTransport(options);
  }

  private async request<T>(path: string, options?: RequestInit): Promise<ApiEnvelope<T>> {
    const res = await this.transport.fetch(path, options);
    return parseGithubEnvelope<T>(res);
  }

  async getPullRequests(): Promise<ApiEnvelope<GitHubPullRequest[]>> {
    return this.request<GitHubPullRequest[]>('/api/github/pulls');
  }

  async getPullRequest(
    idOrNumber: string | number,
  ): Promise<ApiEnvelope<GitHubPullRequest | null>> {
    return this.request<GitHubPullRequest | null>(
      `/api/github/pulls/${encodeURIComponent(idOrNumber)}`,
    );
  }

  async toggleCommentResolution(
    prId: string,
    commentIndex: number,
    resolved?: boolean,
  ): Promise<ApiEnvelope<GitHubPullRequest>> {
    return this.request<GitHubPullRequest>(
      `/api/github/pulls/${encodeURIComponent(prId)}/comments/${commentIndex}/toggle`,
      {
        method: 'POST',
        body: typeof resolved === 'boolean' ? JSON.stringify({ resolved }) : undefined,
      },
    );
  }

  async retryCheckRun(prId: string, checkIndex: number): Promise<ApiEnvelope<GitHubPullRequest>> {
    return this.request<GitHubPullRequest>(
      `/api/github/pulls/${encodeURIComponent(prId)}/checks/${checkIndex}/retry`,
      {
        method: 'POST',
      },
    );
  }

  async reRunAllChecks(prId: string): Promise<ApiEnvelope<GitHubPullRequest>> {
    return this.request<GitHubPullRequest>(
      `/api/github/pulls/${encodeURIComponent(prId)}/checks/rerun`,
      {
        method: 'POST',
      },
    );
  }

  async approvePullRequest(prId: string): Promise<ApiEnvelope<GitHubPullRequest>> {
    return this.request<GitHubPullRequest>(
      `/api/github/pulls/${encodeURIComponent(prId)}/approve`,
      {
        method: 'POST',
      },
    );
  }

  async mergePullRequest(
    prId: string,
    commitMessage?: string,
    mergeStrategy?: GitHubMergeStrategy,
  ): Promise<ApiEnvelope<GitHubPullRequest>> {
    return this.request<GitHubPullRequest>(`/api/github/pulls/${encodeURIComponent(prId)}/merge`, {
      method: 'POST',
      body: JSON.stringify({ commitMessage, mergeStrategy }),
    });
  }

  async generateItemSummary(
    prId: string,
    options?: { force?: boolean },
  ): Promise<ApiEnvelope<{ summary: string; refreshed?: boolean }>> {
    return this.request<{ summary: string; refreshed?: boolean }>(
      `/api/github/pulls/${encodeURIComponent(prId)}/summary`,
      {
        method: 'POST',
        body: JSON.stringify({ force: options?.force === true }),
      },
    );
  }

  async generateAiReview(
    prId: string,
    options?: { force?: boolean },
  ): Promise<ApiEnvelope<AiReviewJob>> {
    return this.request<AiReviewJob>(`/api/github/pulls/${encodeURIComponent(prId)}/ai-review`, {
      method: 'POST',
      body: JSON.stringify({ force: options?.force === true }),
    });
  }

  async getAiReviewJob(jobId: string): Promise<ApiEnvelope<AiReviewJob>> {
    return this.request<AiReviewJob>(`/api/github/ai-review-jobs/${encodeURIComponent(jobId)}`);
  }

  async listAiReviewJobs(
    options: { prId?: string; activeOnly?: boolean } = {},
  ): Promise<ApiEnvelope<AiReviewJob[]>> {
    const params = new URLSearchParams();
    if (options.prId) params.set('prId', options.prId);
    if (options.activeOnly) params.set('active', 'true');
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<AiReviewJob[]>(`/api/github/ai-review-jobs${suffix}`);
  }

  async cancelAiReviewJob(jobId: string): Promise<ApiEnvelope<AiReviewJob>> {
    return this.request<AiReviewJob>(
      `/api/github/ai-review-jobs/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST' },
    );
  }

  async publishAiReview(
    prId: string,
    input: PublishAiReviewInput,
  ): Promise<ApiEnvelope<PublishAiReviewResult>> {
    const path = input.jobId
      ? `/api/github/ai-review-jobs/${encodeURIComponent(input.jobId)}/publish`
      : `/api/github/pulls/${encodeURIComponent(prId)}/ai-review/publish`;
    const body = input.jobId ? { event: input.event, findingIds: input.findingIds } : input;
    return this.request<PublishAiReviewResult>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getConnection(): Promise<ApiEnvelope<GitHubConnectionAck>> {
    return this.request<GitHubConnectionAck>('/api/github/connection');
  }

  async testConnection(): Promise<ApiEnvelope<GitHubConnectionAck>> {
    return this.request<GitHubConnectionAck>('/api/github/connection/test', { method: 'POST' });
  }

  async postConversationComment(
    prId: string,
    markdown: string,
    idempotencyKey?: string,
  ): Promise<ApiEnvelope<unknown>> {
    return this.request<unknown>(`/api/github/pulls/${encodeURIComponent(prId)}/comments`, {
      method: 'POST',
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      body: JSON.stringify({ markdown }),
    });
  }

  async generateConversationCommentDraft(
    prId: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    return this.request<{ draft: string; provider?: string; model?: string }>(
      `/api/github/pulls/${encodeURIComponent(prId)}/comments/draft`,
      {
        method: 'POST',
        body: JSON.stringify({ hint }),
      },
    );
  }
}
